import { DropdownItem, DropdownOptionType } from '@ardoq/dropdown-menu';
import { ArdoqId, ViewIds } from '@ardoq/api-types';
import { ShapeType } from '@ardoq/icons';
import { OnViewSettingsUpdate } from '@ardoq/data-model';
import { settingsBarConsts } from '@ardoq/settings-bar';
import { WILDCARD } from '@ardoq/common-helpers';
import { getLabelWithFieldRepresentation } from '../../textures/TexturePreview';
import { FieldsRegistryItem } from '../../types';
import { getSelectedRelevantDateFieldsOrder } from '../../data/calculations';
import { FIELD_DROPDOWN_OPTION_NAME } from '@ardoq/view-settings';
import { toId, toIds } from '@ardoq/core';

const SHOW_ALL = 'SHOW_ALL';
const HIDE_ALL = 'HIDE_ALL';

const toggleField = (
  fieldIds: ArdoqId[],
  selectedFieldIds: ArdoqId[],
  id: ArdoqId
) => {
  /**
   * selectedFieldIds can contain ids that are not in fieldIds
   * when a user has multiple workspaces open. That's why we can't just return
   * [] on HIDE_ALL because then we would also hide all the fields in the other
   * workspaces, and same with SHOW_ALL.
   */
  switch (id) {
    case SHOW_ALL:
      return [WILDCARD];
    case HIDE_ALL:
      return selectedFieldIds.filter(
        fieldId => !fieldIds.includes(fieldId) && fieldId !== WILDCARD
      );
    default: {
      const showAll = selectedFieldIds.includes(WILDCARD);
      if (showAll || selectedFieldIds.includes(id)) {
        const fieldFilter = (hiddenId: ArdoqId) => hiddenId !== id;
        return showAll
          ? fieldIds.filter(fieldFilter)
          : selectedFieldIds.filter(fieldFilter);
      }
      return selectedFieldIds.concat([id]);
    }
  }
};

const getOptionClickHandler =
  (
    fieldIds: ArdoqId[],
    selectedFieldIds: ArdoqId[],
    id: ArdoqId,
    viewSettingPropertyName: string,
    onViewSettingsUpdate: OnViewSettingsUpdate
  ) =>
  () => {
    const settings = {
      [viewSettingPropertyName]: toggleField(fieldIds, selectedFieldIds, id),
    };
    const persistent = true;
    onViewSettingsUpdate(ViewIds.TIMELINE, settings, persistent);
  };

type GetMultiselectFieldOptionsArgs = {
  fields: FieldsRegistryItem[];
  selectedFieldIds: ArdoqId[];
  viewSettingPropertyName: string;
  onViewSettingsUpdate: OnViewSettingsUpdate;
  getIconName?: (index: number) => ShapeType;
  hideAllLabel?: string;
  showAllLabel?: string;
};

export const getMultiselectFieldOptions = ({
  fields,
  selectedFieldIds,
  viewSettingPropertyName,
  onViewSettingsUpdate,
  getIconName,
  hideAllLabel = 'Hide all',
  showAllLabel = settingsBarConsts.SHOW_ALL,
}: GetMultiselectFieldOptionsArgs): DropdownItem[] => {
  const fieldIds: ArdoqId[] = toIds(fields);

  const showAll =
    selectedFieldIds.includes(WILDCARD) ||
    fieldIds.every(fieldId => selectedFieldIds.includes(fieldId));
  const hideAll =
    !showAll && fieldIds.every(fieldId => !selectedFieldIds.includes(fieldId));

  const orderedSelectedFields = getSelectedRelevantDateFieldsOrder(
    fields,
    selectedFieldIds
  ).map(toId);

  return [
    {
      label: showAllLabel,
      name: 'show_all',
      isActive: showAll,
      onClick: getOptionClickHandler(
        fieldIds,
        selectedFieldIds,
        SHOW_ALL,
        viewSettingPropertyName,
        onViewSettingsUpdate
      ),
      type: DropdownOptionType.OPTION,
    },
    {
      label: hideAllLabel,
      name: 'hide_all',
      isActive: hideAll,
      onClick: getOptionClickHandler(
        fieldIds,
        selectedFieldIds,
        HIDE_ALL,
        viewSettingPropertyName,
        onViewSettingsUpdate
      ),
      type: DropdownOptionType.OPTION,
    },
    {
      type: DropdownOptionType.DIVIDER,
    },
    ...fields.map(field => {
      const selectedFieldIndex = orderedSelectedFields.indexOf(field._id);
      return {
        name: FIELD_DROPDOWN_OPTION_NAME,
        label: getLabelWithFieldRepresentation({
          label: field.label,
          textureIndex: selectedFieldIndex,
          milestoneIconName: getIconName?.(selectedFieldIndex),
        }),
        onClick: getOptionClickHandler(
          fieldIds,
          selectedFieldIds,
          field._id,
          viewSettingPropertyName,
          onViewSettingsUpdate
        ),
        isActive: showAll || selectedFieldIds.includes(field._id),
        type: DropdownOptionType.OPTION,
      };
    }),
  ];
};
